/**
 * Client-side mirror of the backend's config-version resolution.
 *
 * The backend decides what a build sees in two places — `scopeToConfigVersion`
 * for SQL and `pickForConfigVersion` for rows already in memory (see
 * `wcc4-backend/src/modules/game-config/utils/config-version-scope.util.ts`).
 * Admin pages that show "what does a client on this config version actually
 * get?" have to answer the same question, so the rule lives here once rather
 * than being re-derived per page.
 *
 * Keep this in step with the backend util. The rule is small and unlikely to
 * change, but a drift between the two means the admin panel shows an operator
 * something different from what the game serves.
 */

/** Any row on a table that carries the nullable `configVersionId` column. */
export interface ConfigScopedRow {
  /** Null/undefined means the row is shared by every build. */
  configVersionId?: string | null;
}

/**
 * Resolve the rows a client on `configVersionId` is served: its own variant
 * where one exists, otherwise the shared row it would have inherited.
 *
 * Mirrors `pickForConfigVersion` on the backend, including the tie-break — a
 * variant row always beats the shared row for the same key.
 *
 * @param rows every row for the table, shared and variant alike
 * @param configVersionId the version being previewed, or null for "shared only"
 * @param keyOf the business key that a variant replaces its shared row on
 */
export function pickForConfigVersion<T extends ConfigScopedRow>(
  rows: T[],
  configVersionId: string | null | undefined,
  keyOf: (row: T) => string
): T[] {
  const chosen = new Map<string, T>();
  for (const row of rows) {
    const cv = row.configVersionId ?? null;
    // Another build's variant is never visible to this one.
    if (cv !== null && cv !== configVersionId) continue;
    const key = keyOf(row);
    const existing = chosen.get(key);
    if (!existing || (existing.configVersionId ?? null) === null) {
      chosen.set(key, row);
    }
  }
  return [...chosen.values()];
}

/** True when the row is an override targeted at the version being previewed. */
export function isVariantRow<T extends ConfigScopedRow>(
  row: T,
  configVersionId: string | null | undefined
): boolean {
  const cv = row.configVersionId ?? null;
  return cv !== null && cv === (configVersionId ?? null);
}

/**
 * Who a scoped row reaches, for a table column: "All builds" for a shared row,
 * otherwise the config version's label (only builds mapped to it get the row).
 */
export function configVersionScopeLabel(
  row: ConfigScopedRow & { configVersionLabel?: string | null }
): string {
  if ((row.configVersionId ?? null) === null) return 'All builds';
  return row.configVersionLabel ?? 'Unknown config version';
}
